import type { ActionFunction, LoaderFunction, MetaFunction } from '@remix-run/node'
import { json, redirect } from '@remix-run/node'
import { useActionData, useLoaderData, useOutletContext } from '@remix-run/react'
import type { CountryCode } from 'libphonenumber-js'
import { getCountries } from 'react-phone-number-input'
import { stripHtml } from 'string-strip-html'
import i18next from '~/i18next.server'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ValidatedForm, validationError } from 'remix-validated-form'
import type { CreateClientRequestData, Language } from '~/types/api-client'
import ApiClient from '~/services/api-client.server'
import * as Sentry from '@sentry/remix'
import { handleError } from '~/services/error-handler.server'
import { checkAuth } from '~/services/auth.server'
import {
  createHashedEmailCookie,
  createSalesTagCookie,
  createUtmCookie,
  createVoucherCookie,
  deleteAffiliateCxdCookie,
  deleteAffiliateMetaDataCookie,
  deleteAffiliateRefCookie,
  deleteAffiliateSourceIdCookie,
  deleteSalesTagCookie,
  getAffiliateCxdCookie,
  getAffiliateMetaDataCookieParams,
  getAffiliateRefCookieParams,
  getAffiliateSourceIdCookieParams,
  getGtmRelatedTrackingInfoCookies,
  getSalesTagCookieParams,
  getUtmCookieParams,
} from '~/cookies'
import { formalTitles } from '~/helpers/formal-titles'
import CheckboxInput from '~/components/input-types/CheckboxInput'
import PhoneInput from '~/components/input-types/PhoneInput_v2'
import TextInput from '~/components/input-types/TextInput_v2'
import ComboboxInput from '~/components/input-types/ComboboxInput_v2'
import SelectInput from '~/components/input-types/SelectInput_v2'
import SubmitButton from '~/components/payment-accounts/profile-tab/SubmitButton'
import Recaptcha from '~/components/input-types/Recaptcha'
import { CloudflareTurnstile } from '~/components/input-types/CloudflareTurnstile'
import ErrorBox from '~/components/ErrorBox'
import Validator from '~/helpers/validation'
import type KeyValuePair from '~/types/key-value-pair'
import type { HumanVerificationService } from '~/types/shared'
import { getCountriesList } from '~/services/countries.server'
import { NS } from '~/enums'
import { verifyCloudflareTurnstileResponse } from '~/services/cloudflare-turnstile.server'
import { getHumanVerificationMode, getHumanVerificationType } from '~/helpers/human-verification'
import { buildTabTitle } from '~/helpers/string'

type LoaderData = {
  recaptchaSiteKey?: string
  turnstileSiteKey?: string
  email: string | null
  countryCode: string | null
  countries: KeyValuePair[]
  humanVerificationType?: HumanVerificationService
  tabTitle: string
}

export let loader: LoaderFunction = async ({ request, params }) => {
  // create headers
  let headers = new Headers()

  // redirect to / if user is authenticated but don't proceed to authentication if he is not authenticated
  let user = await checkAuth(request, new Headers(), {}, false)
  let t = await i18next.getFixedT(request, NS.UI)

  if (user) {
    const url = new URL(request.url)
    // Voucher cookie
    const voucherCookie = createVoucherCookie(url, request.headers)
    if (voucherCookie) headers.append('Set-Cookie', voucherCookie)
    // Utm cookie
    const utmCookie = createUtmCookie(url, request.headers)
    if (utmCookie) headers.append('Set-Cookie', utmCookie)
    // Sales tag cookie
    const salesTagCookie = createSalesTagCookie(url, request.headers)
    if (salesTagCookie) headers.append('Set-Cookie', salesTagCookie)

    let redirectTo = '/'

    // if rtn=challenges then redirect to challenges page
    if (url.searchParams.get('rtn') === 'challenges') {
      redirectTo = '/challenges'
    }

    return redirect(redirectTo, { headers: headers })
  }

  const url = new URL(request.url)
  const email = url.searchParams.get('email')

  const countries = await getCountriesList(request, headers)

  const { recaptchaSiteKey, turnstileSiteKey } = getHumanVerificationMode(request.headers)
  const humanVerificationType = getHumanVerificationType(request.headers)

  return json<LoaderData>({
    email: email,
    recaptchaSiteKey,
    turnstileSiteKey,
    countryCode: request.headers.get('cf-ipcountry'),
    countries,
    humanVerificationType,
    tabTitle: buildTabTitle(t('sign_up'))
  })
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  return [
    { title: data.tabTitle }
  ]
}

type ActionData = { error: { code: number | string; message: string } }

export const action: ActionFunction = async ({ request }) => {
  // create headers
  let headers = new Headers()

  const locale = await i18next.getLocale(request)

  const formData = await request.formData()
  const humanVerificationType = getHumanVerificationType(request.headers)
  const humanVerificationSystem: HumanVerificationService =
    humanVerificationType ?? (formData.get('countryCode') === 'CN' ? 'CLOUDFLARE_TURNSTILE' : 'GOOGLE_RECAPTCHA')
  const includeCfdDisclaimer = formData.get('country') === 'ES'

  const result = await Validator.signUp(humanVerificationSystem, includeCfdDisclaimer).validate(formData)

  if (result.error) {
    return validationError(result.error)
  }

  if (humanVerificationSystem === 'CLOUDFLARE_TURNSTILE') {
    try {
      const { turnstileSecretKey } = getHumanVerificationMode(request.headers)
      const isTurnstileResponseValid = await verifyCloudflareTurnstileResponse(
        result.data['turnstile'],
        turnstileSecretKey,
      )
      if (!isTurnstileResponseValid) {
        return json<ActionData>({
          error: { code: 400, message: 'Human verification failed. Please try again.' },
        })
      }
    } catch (error) {
      return json<ActionData>({
        error: { code: 500, message: 'An error occurred while processing the verification. Please try again later.' },
      })
    }
  }

  const apiClient = new ApiClient(null, request.headers)

  //Note: get them from cache instead?
  // const displayLanguages = await getDisplayLanguages(request, headers)

  let displayLanguages: Language[] = []

  try {
    displayLanguages = await apiClient.getDisplayLanguages()
  } catch (error: any) {
    const handledError = await handleError(error, request)
    return json<ActionData>({ error: { code: handledError.response?.status ?? 500, message: handledError.message } })
  }

  const requestData: CreateClientRequestData = {
    contact: {
      title: result.data.title,
      firstName: result.data.firstName,
      lastName: result.data.lastName,
      email: result.data.email,
      primaryAddress: { country: result.data.country },
      locale: { id: displayLanguages.find((l) => l.iso6391.toLowerCase() === locale.toLowerCase())?.id ?? 1 },
    },
    sourceType: 'CLIENTAREA',
    clientType: 1, //by default INDIVIDUAL
    tcAcknowledge: result.data.acceptTerms === 'true', //terms & conditions
    ppAcknowledge: result.data.acceptTerms === 'true', //privacy policy
    kycAcknowledge: result.data.acknowledgePersonalData === 'true',
    emailAcknowledge: result.data.subscribeNewsletter === 'true',
    cfdAcknowledge: result.data.cfdDisclaimer === 'true',
    // 'g-recaptcha-response': result.data.recaptcha ?? '',
  }

  if (result.data.phoneNumber) requestData.contact.phoneNumber = result.data.phoneNumber.replace(/\s/g, '')

  const affiliateRefParams = getAffiliateRefCookieParams(request.headers)
  if (affiliateRefParams) requestData['affiliate'] = affiliateRefParams

  const affiliateCxd = getAffiliateCxdCookie(request.headers)
  if (affiliateCxd?.cxd) requestData['affiliate'] = { ...requestData['affiliate'], cxd: affiliateCxd?.cxd }

  const affiliateSourceId = getAffiliateSourceIdCookieParams(request.headers)
  if (affiliateSourceId?.id) requestData['affiliate'] = { ...requestData['affiliate'], sourceId: affiliateSourceId.id }

  const affiliateMetaData = getAffiliateMetaDataCookieParams(request.headers)
  if (affiliateMetaData?.metaData)
    requestData['affiliate'] = { ...requestData['affiliate'], metaData: affiliateMetaData?.metaData }

  const utmParams = getUtmCookieParams(request.headers)
  if (utmParams) requestData['marketingUtm'] = utmParams

  const salesTagParams = getSalesTagCookieParams(request.headers)
  if (salesTagParams) requestData['salesTags'] = salesTagParams

  const gtmTracking = getGtmRelatedTrackingInfoCookies(request.headers)
  if (gtmTracking) requestData['gtmTracking'] = gtmTracking

  requestData['gtmTracking'] = {
    ...requestData['gtmTracking'],
    Cookie: request.headers.get('cookie') ?? '',
  }

  try {
    Sentry.addBreadcrumb({
      category: 'payload',
      message: 'Core API createClient payload.',
      level: 'info',
      data: { requestData: requestData },
    })

    await apiClient.createClient(requestData)

    const hashedEmailCookie = createHashedEmailCookie(result.data.email, request.headers)
    if (hashedEmailCookie) headers.append('Set-Cookie', hashedEmailCookie)
  } catch (error: any) {
    const handledError = await handleError(error, request)
    return json<ActionData>({
      error: {
        code: handledError.response?.headers?.['ttp-error-code']
          ? handledError.response?.headers?.['ttp-error-code']
          : (handledError.response?.status ?? 500),
        message: handledError.message,
      },
    })
  }

  // Delete affiliate and utm cookies
  deleteAffiliateRefCookie(request.headers, headers)
  deleteAffiliateSourceIdCookie(request.headers, headers)
  deleteAffiliateCxdCookie(request.headers, headers)
  deleteAffiliateMetaDataCookie(request.headers, headers)
  deleteSalesTagCookie(request.headers, headers)

  return redirect('/registered', { headers: headers })
}

export let handle = { i18n: ['globals', 'ui', 'personal-info'] }

export default function Signup() {
  let { recaptchaSiteKey, turnstileSiteKey, email, countryCode, countries, humanVerificationType } =
    useLoaderData<LoaderData>()
  let actionData = useActionData<ActionData>()

  let { t } = useTranslation()
  let title = t('sign_up', { ns: NS.UI })
  let formalTitle = t('personal_title', { ns: NS.PERSONAL_INFO })
  let firstName = t('first_name', { ns: NS.PERSONAL_INFO })
  let lastName = t('last_name', { ns: NS.PERSONAL_INFO })
  let emailInput = t('email', { ns: NS.PERSONAL_INFO })
  let country = t('country', { ns: NS.PERSONAL_INFO })
  let phoneNumber = t('phone', { ns: NS.PERSONAL_INFO })
  let acceptTerms = t('terms_consent', { ns: NS.UI })
  let acknowledgePersonalData = t('personal_data_consent', { ns: NS.UI })
  let subscribeNewsletter = t('newsletter_consent', { ns: NS.UI })
  let cfdDisclaimer = t('cfd_disclaimer', { ns: NS.UI })
  let signUp = t('sign_up', { ns: NS.UI })
  let alreadyRegistered = t('already_have_an_account', { ns: NS.UI })
  let accountAlreadyExists = t('account_already_exists', { ns: NS.UI })
  let err_400CL4 = t('error_400cl4', { ns: NS.UI })

  const outletContext: { mainDomainUrl: string } = useOutletContext()
  const mainDomainUrl = outletContext.mainDomainUrl
  const acceptTermsWithUrls = acceptTerms
    .replace('{{termsConditionsUrl}}', `${mainDomainUrl}/terms-conditions`)
    .replace('{{privacyPolicyUrl}}', `${mainDomainUrl}/privacy`)

  let [defaultCountry, setDefaultCountry] = useState<string | undefined>()

  const humanVerificationSystem: HumanVerificationService =
    humanVerificationType ?? (countryCode?.toUpperCase() === 'CN' ? 'CLOUDFLARE_TURNSTILE' : 'GOOGLE_RECAPTCHA')

  function handleCountryOnChange(value: string) {
    // Check if country code is valid before setting the defaultCountry
    if (getCountries().includes(value as CountryCode)) setDefaultCountry(value)
  }

  function setErrorMessage(errorCode: string | number, httpErrorMessage: string) {
    switch (errorCode) {
      case 'BR-70002':
        return err_400CL4
      case 'CL-3002':
        return accountAlreadyExists
      default:
        return httpErrorMessage
    }
  }

  return (
    <div className="px-5 py-8 lg:flex lg:justify-center lg:gap-20 2xl:gap-[167px] 2xl:pl-[178px] 2xl:pr-[220px]">
      <div className="flex flex-col gap-5 lg:gap-10">
        <div>
          <h1 className="text-xl font-semibold text-brand-black lg:text-5xl">{title}</h1>
          <p className="mt-3 text-sm lg:mt-6 lg:text-2xl 2xl:text-3xl">
            Master the Markets: Become a Professional Prop Trader.
          </p>
        </div>
        <img
          className="hidden max-h-[697px] min-h-[300px] self-stretch lg:block xl:max-w-screen-sm 2xl:h-[534px] 2xl:w-[829px] 2xl:max-w-full 2xl:object-contain"
          src="/images/signup.webp"
          alt="signup"
        />
      </div>
      <ValidatedForm
        className="mt-8 flex flex-shrink-0 flex-col gap-6 lg:mt-0 lg:w-[437px]"
        validator={Validator.signUp(humanVerificationSystem, defaultCountry === 'ES')}
        method="post"
      >
        <SelectInput id="title" name="title" label={formalTitle} renderSelected={(v: string) => v}>
          {formalTitles.map((title) => (
            <SelectInput.Item key={title.key} value={title.key}>
              {title.value}
            </SelectInput.Item>
          ))}
        </SelectInput>

        <TextInput id="firstName" name="firstName" label={firstName} />

        <TextInput id="lastName" name="lastName" label={lastName} />

        <TextInput id="email" name="email" value={email ?? undefined} label={emailInput} />

        <ComboboxInput
          id="country"
          name="country"
          label={country}
          values={countries}
          onChange={handleCountryOnChange}
        />

        <PhoneInput
          id="phoneNumber"
          name="phoneNumber"
          defaultCountry={defaultCountry as CountryCode}
          label={phoneNumber}
          optional
        />

        <div className="mt-2 flex flex-col gap-2.5">
          <CheckboxInput id="acceptTerms" name="acceptTerms" label={acceptTermsWithUrls} />
          <CheckboxInput id="acknowledgePersonalData" name="acknowledgePersonalData" label={acknowledgePersonalData} />
          <CheckboxInput id="subscribeNewsletter" name="subscribeNewsletter" label={subscribeNewsletter} />
          {defaultCountry === 'ES' && <CheckboxInput id="cfdDisclaimer" name="cfdDisclaimer" label={cfdDisclaimer} />}
        </div>

        <input type="hidden" name="countryCode" value={countryCode?.toUpperCase() ?? ''} />

        <div className="mt-2 flex flex-col items-center gap-2 empty:hidden">
          {humanVerificationSystem === 'GOOGLE_RECAPTCHA' ? (
            <Recaptcha siteKey={recaptchaSiteKey} name="recaptcha" />
          ) : humanVerificationSystem === 'CLOUDFLARE_TURNSTILE' ? (
            <CloudflareTurnstile cloudflareTurnstileSiteKey={turnstileSiteKey} name="turnstile" />
          ) : null}
        </div>

        {actionData?.error && (
          <ErrorBox message={setErrorMessage(actionData.error.code, actionData.error.message ?? '')} />
        )}

        <div className="mt-2">
          <SubmitButton label={signUp} buttonProps={{ fullWidth: true, size: 'lg' }} />
        </div>

        <div
          className="mt-2 text-center text-sm text-brand-gray-700 prose-a:border-b prose-a:border-brand-primary-500 prose-a:font-semibold"
          dangerouslySetInnerHTML={{ __html: stripHtml(alreadyRegistered, { ignoreTags: ['a'] }).result }}
        />
      </ValidatedForm>
    </div>
  )
}
